import { DragDropModule } from '@angular/cdk/drag-drop';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { environment } from '@environments/environment';
import { IonicModule } from '@ionic/angular';
import { AngularEditorModule } from '@kolkov/angular-editor';
import { TranslateModule } from '@ngx-translate/core';
import { NgxQRCodeModule } from '@techiediaries/ngx-qrcode';
import { SignaturePadModule } from 'angular2-signaturepad';
import { IonicSelectableModule } from 'ionic-selectable';
import { CURRENCY_MASK_CONFIG, CurrencyMaskModule } from 'ng2-currency-mask';
import { ImageCropperModule } from 'ngx-image-cropper';
import { NgxPaginationModule } from 'ngx-pagination';
import { AccordionInverseComponent } from './components/accordion-inverse/accordion-inverse.component';
import { AccordionComponent } from './components/accordion/accordion.component';
import { ActionSheetMapButtonComponent } from './components/action-sheet-map-button/action-sheet-map-button.component';
import { AvatarCardComponent } from './components/avatar-card/avatar-card.component';
import { ButtonLanguageSelect } from './components/button-language-select/button-language-select.component';
import { PopoverLanguagesComponent } from './components/button-language-select/popover-languages/popover-languages.component';
import { CardArrowComponent } from './components/card-arrow/card-arrow.component';
import { CardPlansListComponent } from './components/card-plans-list/card-plans-list.component';
import { CounterComponent } from './components/counter/counter.component';
import { DetailCardComponent } from './components/detail-card/detail-card.component';
import { DetailImmobileComponent } from './components/detail-immobile/detail-immobile.component';
import { DetailInterveningComponent } from './components/detail-intervening/detail-intervening.component';
import { DetailItemsInformationsComponent } from './components/detail-items-informations/detail-items-informations.component';
import { DetailItemsMeasuresComponent } from './components/detail-items-measures/detail-items-measures.component';
import { DetailModulesCardComponent } from './components/detail-modules-card/detail-modules-card.component';
import { DetailWitnessComponent } from './components/detail-witness/detail-witness.component';
import { DetailsItemsKeysControlComponent } from './components/details-items-keys-control/details-items-keys-control.component';
import { DetailsItemsResponsibleComponent } from './components/details-items-responsible/details-items-responsible.component';
import { DynamicInputFormComponent } from './components/dynamic-input-form/dynamic-input-form.component';
import { AddressFormComponent } from './components/forms/address-form/address-form.component';
import { CompanyInformationsFormComponent } from './components/forms/company-informations-form/company-informations-form.component';
import { PersonalInformationFormComponent } from './components/forms/personal-information-form/personal-information-form.component';
import { ProcessFormComponent } from './components/forms/process-form/process-form.component';
import { SurveyConsumptionMeasureFormComponent } from './components/forms/survey-consumption-measure-form/survey-consumption-measure-form.component';
import { SurveyImmobileFormComponent } from './components/forms/survey-immobile-form/survey-immobile-form.component';
import { SurveyInformationsFormComponent } from './components/forms/survey-informations-form/survey-informations-form.component';
import { AccordionInterveningFormComponent } from './components/forms/survey-intervening-form/components/accordion-intervening-form/accordion-intervening-form.component';
import { SurveyChoiceInterveningModalComponent } from './components/forms/survey-intervening-form/components/survey-choice-intervening-modal/survey-choice-intervening-modal.component';
import { SurveyDetailsAccordionComponent } from './components/forms/survey-intervening-form/components/survey-details-accordion/survey-details-accordion.component';
import { SurveyInterveningFormComponent } from './components/forms/survey-intervening-form/survey-intervening-form.component';
import { SurveyItemFormComponent } from './components/forms/survey-item-form/survey-item-form.component';
import { SurveyKeysAndControlsFormComponent } from './components/forms/survey-keys-and-controls-form/survey-keys-and-controls-form.component';
import { SurveyResponsibleFormComponent } from './components/forms/survey-responsible-form/survey-responsible-form.component';
import { SurveySignatureFormComponent } from './components/forms/survey-signature-form/survey-signature-form.component';
import { SurveyWitnessFormComponent } from './components/forms/survey-witness-form/survey-witness-form.component';
import { InformCustomerComponent } from './components/inform-customer/inform-customer.component';
import { ListAvatarCardComponent } from './components/list-avatar-card/list-avatar-card.component';
import { ListRowCardComponent } from './components/list-row-card/list-row-card.component';
import { ListSmallCardsComponent } from './components/list-small-cards/list-small-cards.component';
import { ModalElementsListComponent } from './components/main/inspect/modal-elements/modal-elements-list.component';
import { TemplatesListComponent } from './components/main/inspect/select-template/components/templates-list/templates-list.component';
import { SelectTemplateComponent } from './components/main/inspect/select-template/select-template.component';
import { SetUpTemplateComponent } from './components/main/inspect/set-up-template/set-up-template.component';
import { MenuComponent } from './components/menu/menu.component';
import { CameraModalComponent } from './components/modals/camera-modal/camera-modal.component';
import { CameraVideoModalComponent } from './components/modals/camera-video-modal/camera-video-modal.component';
import { ChangePasswordModalComponent } from './components/modals/change-password-modal/change-password-modal.component';
import { ContestationModalComponent } from './components/modals/contestation-modal/contestation-modal.component';
import { ImageEditModalComponent } from './components/modals/image-edit-modal/image-edit-modal.component';
import { ImageModalComponent } from './components/modals/image-modal/image-modal.component';
import { ImagePreviewModalComponent } from './components/modals/image-preview-modal/image-preview-modal.component';
import { InputModalComponent } from './components/modals/input-modal/input-modal.component';
import { ModalListDataComponent } from './components/modals/modal-list-data/modal-list-data.component';
import { NoticeAfterLoginModalComponent } from './components/modals/notice-after-login-modal/notice-after-login-modal.component';
import { QrCodeModalComponent } from './components/modals/qr-code-modal/qr-code-modal.component';
import { ReportBugComponent } from './components/modals/report-bug/report-bug.component';
import { SelectSurveyorModalComponent } from './components/modals/select-surveyor-modal/select-surveyor-modal.component';
import { SendSurveyModalComponent } from './components/modals/send-survey-modal/send-survey-modal.component';
import { SortModalComponent } from './components/modals/sort-modal/sort-modal.component';
import { SurveyModalComponent } from './components/modals/survey-modal/survey-modal.component';
import { VideoModalComponent } from './components/modals/video-modal/video-modal.component';
import { UseKeyboardMicModalComponent } from './components/modals/use-keyboard-mic-modal/use-keyboard-mic-modal.component';
import { OneLineAvatarCardComponent } from './components/one-line-avatar-card/one-line-avatar-card.component';
import { OneLineCardComponent } from './components/one-line-card/one-line-card.component';
import { PhotoGalleryComponent } from './components/photo-gallery/photo-gallery.component';
import { PopoverComponent } from './components/popover/popover.component';
import { ProfilePhotoCircleComponent } from './components/profile-photo-circle/profile-photo-circle.component';
import { QrCodeReaderComponent } from './components/qr-code-reader/qr-code-reader.component';
import { ReportInformationsComponent } from './components/report-informations/report-informations.component';
import { PopoverAdministratorComponent } from './components/row-card/popovers/popover-administrator/popover-administrator.component';
import { RowCardComponent } from './components/row-card/row-card.component';
import { SearchBarComponent } from './components/search-bar/search-bar.component';
import { SharedSettingsComponent } from './components/shared-settings/shared-settings.component';
import { SignatureButtonComponent } from './components/signature-button/signature-button.component';
import { SignatureComponent } from './components/signature/signature.component';
import { SpeechInputComponent } from './components/speech-input/speech-input.component';
import { SubHeaderTextComponent } from './components/sub-header-text/sub-header-text.component';
import { TabsComponent } from './components/tabs-Component/tabs.component';
import { TextareaSpeechInputComponent } from './components/textarea-speech-input/textarea-speech-input.component';
import { TwoLinesCardComponent } from './components/two-lines-card/two-lines-card.component';
import { TwoLinesPinCardComponent } from './components/two-lines-pin-card/two-lines-pin-card.component';
import { DropSpecialCharactersDirective } from './directives/drop-special-character.directive';
import { IonicMaskDirective } from './directives/ionic-mask.directive';
import { ModalDeleteAccountComponent } from './modals/modal-delete-account/modal-delete-account/modal-delete-account.component';
import { ModalNPSComponent } from './modals/modal-nps/modal-nps.component';
import { ModalPhotoGuideComponent } from './modals/modal-photo-guide/modal-photo-guide.component';
import { DoneTextPipe } from './pipes/done-text.pipe';
import { IonicMaskPipe } from './pipes/ionic-mask.pipe';
import { PendenciesTextPipe } from './pipes/pendencies-text.pipe';
import { AuthenticationService } from './providers/authentication.service';
import { IndicationModalComponent } from './components/modals/indication-modal/indication-modal.component';
import { RequestSignatureModalComponent } from './components/modals/request-signature-modal/request-signature-modal.component';
import { PartialReportModalComponent } from './components/modals/partial-report-modal/partial-report-modal.component';
import { CalendarInputComponent } from './components/calendar-input/calendar-input.component';
import { DetailSignaturesComponent } from './components/detail-signatures/detail-signatures.component';

@NgModule({
  declarations: [
    PopoverLanguagesComponent,
    DropSpecialCharactersDirective,
    ButtonLanguageSelect,
    SurveyDetailsAccordionComponent,
    IonicMaskDirective,
    SurveyChoiceInterveningModalComponent,
    SignatureComponent,
    IonicMaskPipe,
    MenuComponent,
    ProfilePhotoCircleComponent,
    SelectTemplateComponent,
    TabsComponent,
    TemplatesListComponent,
    SetUpTemplateComponent,
    AvatarCardComponent,
    SearchBarComponent,
    ListAvatarCardComponent,
    RowCardComponent,
    ListRowCardComponent,
    PopoverAdministratorComponent,
    PersonalInformationFormComponent,
    AccordionComponent,
    CounterComponent,
    TwoLinesCardComponent,
    OneLineCardComponent,
    InputModalComponent,
    TwoLinesPinCardComponent,
    PendenciesTextPipe,
    AddressFormComponent,
    CalendarInputComponent,
    CompanyInformationsFormComponent,
    OneLineAvatarCardComponent,
    SurveyInformationsFormComponent,
    DoneTextPipe,
    SubHeaderTextComponent,
    ModalElementsListComponent,
    ListSmallCardsComponent,
    InputModalComponent,
    OneLineCardComponent,
    TwoLinesCardComponent,
    SpeechInputComponent,
    DynamicInputFormComponent,
    PhotoGalleryComponent,
    ImageModalComponent,
    ImagePreviewModalComponent,
    CardArrowComponent,
    DetailItemsInformationsComponent,
    DetailModulesCardComponent,
    SignatureButtonComponent,
    ReportInformationsComponent,
    SelectSurveyorModalComponent,
    SurveyItemFormComponent,
    SurveyConsumptionMeasureFormComponent,
    SurveyKeysAndControlsFormComponent,
    SurveyResponsibleFormComponent,
    SurveySignatureFormComponent,
    PopoverComponent,
    ModalListDataComponent,
    DetailCardComponent,
    DetailsItemsKeysControlComponent,
    DetailItemsMeasuresComponent,
    DetailsItemsResponsibleComponent,
    DetailSignaturesComponent,
    ChangePasswordModalComponent,
    AccordionInverseComponent,
    SurveyModalComponent,
    VideoModalComponent,
    SurveyImmobileFormComponent,
    SurveyInterveningFormComponent,
    DetailInterveningComponent,
    DetailImmobileComponent,
    ProcessFormComponent,
    ImageEditModalComponent,
    DetailWitnessComponent,
    SurveyWitnessFormComponent,
    SharedSettingsComponent,
    InformCustomerComponent,
    ContestationModalComponent,
    ReportBugComponent,
    IndicationModalComponent,
    CameraModalComponent,
    CameraVideoModalComponent,
    CardPlansListComponent,
    ActionSheetMapButtonComponent,
    SortModalComponent,
    QrCodeModalComponent,
    QrCodeReaderComponent,
    AccordionInterveningFormComponent,
    TextareaSpeechInputComponent,
    UseKeyboardMicModalComponent,
    NoticeAfterLoginModalComponent,
    ModalDeleteAccountComponent,
    ModalPhotoGuideComponent,
    ModalNPSComponent,
    SendSurveyModalComponent,
    RequestSignatureModalComponent,
    PartialReportModalComponent,
  ],
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    ReactiveFormsModule,
    HttpClientModule,
    SignaturePadModule,
    NgxPaginationModule,
    IonicSelectableModule,
    CurrencyMaskModule,
    ImageCropperModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatInputModule,
    MatFormFieldModule,
    DragDropModule,
    ScrollingModule,
    TranslateModule,
    NgxQRCodeModule,
    AngularEditorModule,
  ],
  exports: [
    CommonModule,
    FormsModule,
    IonicModule,
    IonicMaskPipe,
    ReactiveFormsModule,
    HttpClientModule,
    SignatureComponent,
    MenuComponent,
    ProfilePhotoCircleComponent,
    AvatarCardComponent,
    SearchBarComponent,
    ListAvatarCardComponent,
    RowCardComponent,
    ListRowCardComponent,
    PopoverAdministratorComponent,
    SelectTemplateComponent,
    TemplatesListComponent,
    SetUpTemplateComponent,
    PersonalInformationFormComponent,
    AccordionComponent,
    CounterComponent,
    TwoLinesCardComponent,
    TabsComponent,
    OneLineCardComponent,
    InputModalComponent,
    NgxPaginationModule,
    TwoLinesPinCardComponent,
    PendenciesTextPipe,
    AddressFormComponent,
    CalendarInputComponent,
    CompanyInformationsFormComponent,
    OneLineAvatarCardComponent,
    SurveyInformationsFormComponent,
    DoneTextPipe,
    SubHeaderTextComponent,
    ModalElementsListComponent,
    ListSmallCardsComponent,
    InputModalComponent,
    OneLineCardComponent,
    TwoLinesCardComponent,
    NgxPaginationModule,
    TabsComponent,
    SpeechInputComponent,
    PhotoGalleryComponent,
    DynamicInputFormComponent,
    ImageModalComponent,
    CardArrowComponent,
    DetailItemsInformationsComponent,
    DetailModulesCardComponent,
    SignatureButtonComponent,
    ReportInformationsComponent,
    SelectSurveyorModalComponent,
    SurveyItemFormComponent,
    SurveyConsumptionMeasureFormComponent,
    SurveyKeysAndControlsFormComponent,
    SurveyResponsibleFormComponent,
    SurveySignatureFormComponent,
    PopoverComponent,
    ModalListDataComponent,
    DetailCardComponent,
    DetailsItemsKeysControlComponent,
    DetailItemsMeasuresComponent,
    DetailsItemsResponsibleComponent,
    DetailSignaturesComponent,
    ChangePasswordModalComponent,
    AccordionInverseComponent,
    SurveyModalComponent,
    VideoModalComponent,
    SurveyImmobileFormComponent,
    SurveyInterveningFormComponent,
    DetailInterveningComponent,
    DetailImmobileComponent,
    ProcessFormComponent,
    ImageEditModalComponent,
    DetailWitnessComponent,
    SurveyWitnessFormComponent,
    SharedSettingsComponent,
    InformCustomerComponent,
    MatDatepickerModule,
    MatNativeDateModule,
    MatInputModule,
    MatFormFieldModule,
    ContestationModalComponent,
    ReportBugComponent,
    IndicationModalComponent,
    ImagePreviewModalComponent,
    SignaturePadModule,
    IonicSelectableModule,
    CameraModalComponent,
    CameraVideoModalComponent,
    CardPlansListComponent,
    ActionSheetMapButtonComponent,
    IonicMaskDirective,
    SortModalComponent,
    DragDropModule,
    ScrollingModule,
    TranslateModule,
    ButtonLanguageSelect,
    NgxQRCodeModule,
    QrCodeModalComponent,
    QrCodeReaderComponent,
    TextareaSpeechInputComponent,
    DropSpecialCharactersDirective,
    UseKeyboardMicModalComponent,
    NoticeAfterLoginModalComponent,
    ModalDeleteAccountComponent,
    ModalPhotoGuideComponent,
    RequestSignatureModalComponent,
    ModalNPSComponent,
  ],
  providers: [
    AuthenticationService,
    { provide: CURRENCY_MASK_CONFIG, useValue: environment.customCurrencyMaskConfig },
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SharedModule {}
